import { FormikValues } from 'formik/dist/types';

import { useDefaultSetItemRestSec } from '../../../hooks/use-default-set-item-rest-sec';
import { SetType, TrainingSetItemStatus } from '../../../shared/constants';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { IProgramSetItem } from '../../../types/interfaces/program-set-item';
import { ITrainingSet } from '../../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../../types/interfaces/training-set-item';
import { orderBy, replace } from '../../../utils/collections/array-utils';
import {
  changeSetNumber,
  generateTemporaryId,
  getEmptyTrainingSet,
  orderSets,
} from '../../../utils/training/set-utils';

export function useEditTraining(formik: FormikValues, trainingId?: string) {
  const { defaultRestForNewEmptyItem } = useDefaultSetItemRestSec();
  function onChangeTrainingSet(value: ITrainingSet) {
    formik.setFieldValue(
      'trainingSets',
      replace(formik.values.trainingSets, value, ['setNumber', 'orderNumber']),
    );
  }

  function onRemoveTrainingSet({
    setNumber,
    orderNumber,
  }: {
    setNumber: number;
    orderNumber?: number;
  }) {
    const filteredItems = formik.values.trainingSets.filter(
      (trainingSet) =>
        !(
          trainingSet.setNumber === setNumber &&
          (!orderNumber || trainingSet.orderNumber === orderNumber)
        ),
    );

    const isChangeSetNumber = !filteredItems.find(
      (item) => item.setNumber === setNumber,
    );

    const recalculatedSets = filteredItems.map((item) => {
      const newSetNumber = item.setNumber;
      let newOrderNumber = item.orderNumber;

      if (isChangeSetNumber && item.setNumber > setNumber) {
        newOrderNumber = item.setNumber - 1;
      }

      if (item.setNumber === setNumber && item.orderNumber > orderNumber) {
        newOrderNumber = item.orderNumber - 1;
      }

      return {
        ...item,
        setNumber: newSetNumber,
        orderNumber: newOrderNumber,
      };
    });

    formik.setFieldValue('trainingSets', recalculatedSets);
  }

  function onDuplicateTrainingSet(setNumber: number) {
    const sameSetItems = formik.values.trainingSets.filter(
      (set) => set.setNumber === setNumber,
    );
    const maxOrderNumber = sameSetItems
      ? Math.max(...sameSetItems.map((set) => set.orderNumber))
      : -1;
    const setWithMaxOrder = sameSetItems.find(
      (set) => set.orderNumber === maxOrderNumber,
    );

    const newSet = { ...setWithMaxOrder, orderNumber: maxOrderNumber + 1 };
    delete newSet.id;

    newSet.trainingSetItems.forEach((item) => {
      item.id && delete item.id;
      item.trainingSetItemOptions.forEach((option) => {
        option.id && delete option.id;
      });
    });

    formik.setFieldValue(
      'programSets',
      orderBy(
        [...formik.values.trainingSets, newSet],
        ['setNumber', 'orderNumber'],
      ),
    );
  }

  function getSetIndex(
    trainingSetGroups: { [key: string]: ITrainingSet[] },
    index: number,
  ): number {
    let value = 0;

    for (let i = 0; i < index; i++) {
      value += trainingSetGroups[i]?.length;
    }

    return value;
  }

  function getEmptyTrainingSetLocal(
    currentTrainingSets: ITrainingSet[],
    trainingId: string = null,
  ): ITrainingSet {
    const maxSetNumber = currentTrainingSets?.length
      ? Math.max(...currentTrainingSets.map((set) => set.setNumber))
      : -1;

    return getEmptyTrainingSet(
      maxSetNumber + 1,
      0,
      trainingId,
      defaultRestForNewEmptyItem,
    );
  }

  function onAddEmptyTrainingSet(trainingId: string = null) {
    const emptyTrainingSet = getEmptyTrainingSetLocal(
      formik.values.trainingSets,
      trainingId,
    );

    formik.setFieldValue('trainingSets', [
      ...(formik.values.trainingSets || []),
      emptyTrainingSet,
    ]);
  }

  function onAddTrainingSetFromSavedProgramSet(
    programSets: IProgramSet[],
    isSuperset: boolean,
  ) {
    const maxSetNumber = formik.values.trainingSets?.length
      ? Math.max(
          ...formik.values.trainingSets.map(
            (set: ITrainingSet) => set.setNumber,
          ),
        )
      : -1;

    let setOrderNumber = 0;
    let setNumber = maxSetNumber + 1;
    const result: ITrainingSet[] = [];

    programSets.forEach((programSet) => {
      let itemOrderNumber = 0;
      const newTrainingSet = {
        exerciseId: programSet.exerciseId,
        exercise: programSet.exercise,
        setNumber: setNumber,
        orderNumber: setOrderNumber,
        trainingId: trainingId,
        temporaryId: generateTemporaryId(),
        trainingSetItems: programSet.programSetItems.flatMap(
          (item: IProgramSetItem): ITrainingSetItem[] => {
            const result = [];

            for (let i = 0; i < item.sets; i++) {
              result.push({
                orderNumber: itemOrderNumber,
                rest: item.rest,
                status: TrainingSetItemStatus.notStarted,
                type: item.type || SetType.normal,
                temporaryId: generateTemporaryId(),
                trainingSetItemOptions: item.programSetItemOptions.map(
                  (setItemOption) => ({
                    optionId: setItemOption.optionId,
                    value: null,
                    value2: null,
                    programValue: setItemOption.value,
                    programValue2: setItemOption.value2,
                  }),
                ),
              });
              itemOrderNumber++;
            }

            return result;
          },
        ),
      };

      if (isSuperset) {
        setOrderNumber++;
      } else {
        setNumber++;
      }

      result.push(newTrainingSet);
    });

    formik.setFieldValue(
      'trainingSets',
      orderSets([...(formik.values.trainingSets || []), ...result]),
    );
  }

  function onChangeSetOrder(setNumber: number, direction: 'up' | 'down') {
    const maxSetNumber = Math.max(
      ...formik.values.trainingSets.map((set) => set.setNumber),
    );

    if (
      (direction === 'up' && setNumber <= 0) ||
      (direction === 'down' && setNumber >= maxSetNumber)
    )
      return;

    const updatedProgramSets = changeSetNumber(
      formik.values.trainingSets,
      setNumber,
      direction,
    );

    formik.setFieldValue('trainingSets', updatedProgramSets);
  }

  return {
    onChangeTrainingSet,
    onDuplicateTrainingSet,
    onRemoveTrainingSet,
    onAddEmptyTrainingSet,
    onChangeSetOrder,
    onAddTrainingSetFromSavedProgramSet,
    getSetIndex,
    getEmptyTrainingSet: getEmptyTrainingSetLocal,
  };
}
